(function initSharedCartTracking() {
  if (window.SharedCartTracking) return;

  const SHARED_CART_TRACKING_STORAGE_KEY = 'shared_cart_tracking_events';
  const SHARED_CART_SESSION_CONTEXT_KEY = 'shared_cart_session_context';

  const normalizeMarketCode = (value) => String(value || '').trim().toUpperCase();

  const getSharedCartRegion = (sharedCartUrl) => {
    if (!sharedCartUrl) return '';
    try {
      const url = new URL(sharedCartUrl);
      const host = String(url.hostname || '').trim();
      return normalizeMarketCode((host.split('.')[0] || '').trim());
    } catch (_e) {
      return '';
    }
  };

  const getSourceMarketForAnalytics = (sharedCartData) => {
    const useUrlRegionCompare = !!window.KROWN?.settings?.shared_cart?.include_market_in_url;
    const sourceFromMarketKey = normalizeMarketCode(sharedCartData?.market || '');
    const sourceFromUrlRegion = getSharedCartRegion(sharedCartData?.current_url);
    if (useUrlRegionCompare) return sourceFromUrlRegion;
    return sourceFromMarketKey || sourceFromUrlRegion;
  };

  const getDestinationMarketForAnalytics = () => {
    return normalizeMarketCode(window.KROWN?.settings?.current_country_code || '');
  };

  const getSourceLanguage = (sharedCartData) => {
    const fromPayload = String(sharedCartData?.locale || sharedCartData?.language || '').trim();
    if (fromPayload) return fromPayload.toLowerCase();
    try {
      const url = new URL(sharedCartData?.current_url || '');
      const localeMatch = url.pathname.match(/^\/([a-z]{2}(?:-[A-Z]{2})?)(?:\/|$)/);
      if (localeMatch && localeMatch[1]) return localeMatch[1].toLowerCase();
    } catch (_e) {}
    return '';
  };

  const getDestinationLanguage = () => {
    const htmlLang = String(document.documentElement?.lang || '').trim();
    if (htmlLang) return htmlLang.toLowerCase();
    const pathMatch = window.location.pathname.match(/^\/([a-z]{2}(?:-[A-Z]{2})?)\//);
    return pathMatch && pathMatch[1] ? pathMatch[1].toLowerCase() : '';
  };

  const getCookieValue = (name) => {
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : '';
  };

  const getPartnerIdForAnalytics = (sharedCartData) => {
    const fromCart = String(sharedCartData?.attributes?._partner_id || '').trim();
    if (fromCart) return fromCart;
    const fromCookie = String(getCookieValue('partnerID') || '').trim();
    if (fromCookie) return fromCookie;
    try {
      const rawPartnerInfo = localStorage.getItem('partnerInfo');
      if (!rawPartnerInfo) return '';
      const parsed = JSON.parse(rawPartnerInfo);
      return String(parsed?.commSysPartnerID || '').trim();
    } catch (_e) {
      return '';
    }
  };

  const getUnavailableGlobalCustomIds = (sharedCartData, unavailableVariantIds = []) => {
    const unavailableSet = new Set(
      (unavailableVariantIds || [])
        .map((id) => parseInt(String(id || '').trim(), 10))
        .filter((id) => Number.isFinite(id))
    );
    if (unavailableSet.size === 0) return [];

    const sourceItems = Array.isArray(sharedCartData?.items) ? sharedCartData.items : [];
    const codes = sourceItems
      .filter((item) => unavailableSet.has(parseInt(String(item?.variant_id || ''), 10)))
      .map((item) => String(item?.global_custom_id || '').trim())
      .filter(Boolean);

    return [...new Set(codes)];
  };

  const getPriceChangeGlobalCustomIds = (sharedCartData, priceChangeVariantIds = []) => {
    const priceChangeSet = new Set(
      (priceChangeVariantIds || [])
        .map((id) => parseInt(String(id || '').trim(), 10))
        .filter((id) => Number.isFinite(id))
    );
    if (priceChangeSet.size === 0) return [];

    const sourceItems = Array.isArray(sharedCartData?.items) ? sharedCartData.items : [];
    const codes = sourceItems
      .filter((item) => priceChangeSet.has(parseInt(String(item?.variant_id || ''), 10)))
      .map((item) => String(item?.global_custom_id || '').trim())
      .filter(Boolean);

    return [...new Set(codes)];
  };

  const getTrackedSharedCartEvents = () => {
    try {
      const raw = sessionStorage.getItem(SHARED_CART_TRACKING_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (_e) {
      return [];
    }
  };

  const markSharedCartEventTracked = (eventKey) => {
    if (!eventKey) return;
    const tracked = new Set(getTrackedSharedCartEvents());
    tracked.add(eventKey);
    try {
      sessionStorage.setItem(SHARED_CART_TRACKING_STORAGE_KEY, JSON.stringify([...tracked]));
    } catch (_e) {}
  };

  const isSharedCartEventTracked = (eventKey) => {
    if (!eventKey) return false;
    return getTrackedSharedCartEvents().includes(eventKey);
  };

  const buildSharedCartTrackingKey = (cartToken, eventName, action = '') => {
    const token = String(cartToken || '').trim();
    const evt = String(eventName || '').trim();
    const act = String(action || '').trim();
    return [token, evt, act].filter(Boolean).join('::');
  };

  const pushSharedCartAnalyticsEvent = (eventName, payload = {}, dedupeKey = '') => {
    if (!eventName) return;
    if (dedupeKey && isSharedCartEventTracked(dedupeKey)) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: eventName,
      ...payload
    });
    if (dedupeKey) {
      markSharedCartEventTracked(dedupeKey);
    }
  };

  const setSharedCartSessionContext = (context = {}) => {
    const normalized = {
      cart_token: String(context.cart_token || '').trim(),
      source_market: String(context.source_market || '').trim().toLowerCase(),
      destination_market: String(context.destination_market || '').trim().toLowerCase(),
      source_language: String(context.source_language || '').trim().toLowerCase(),
      destination_language: String(context.destination_language || '').trim().toLowerCase(),
      partner_id: String(context.partner_id || '').trim()
    };
    window.sharedCartSessionContext = normalized;
    try {
      sessionStorage.setItem(SHARED_CART_SESSION_CONTEXT_KEY, JSON.stringify(normalized));
    } catch (_e) {}
  };

  const getSharedCartSessionContext = () => {
    if (window.sharedCartSessionContext) return window.sharedCartSessionContext;
    try {
      const raw = sessionStorage.getItem(SHARED_CART_SESSION_CONTEXT_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (parsed && typeof parsed === 'object') {
        window.sharedCartSessionContext = parsed;
        return parsed;
      }
    } catch (_e) {}
    return null;
  };

  const toCartQuantityMap = (cart) => {
    const quantityMap = new Map();
    const items = Array.isArray(cart?.items) ? cart.items : [];
    for (const item of items) {
      const variantId = parseInt(String(item?.variant_id || ''), 10);
      if (!Number.isFinite(variantId)) continue;
      quantityMap.set(variantId, Number(item?.quantity) || 0);
    }
    return quantityMap;
  };

  const getLineGlobalCustomId = (lineItem) => {
    const fromPayload = String(lineItem?.global_custom_id || '').trim();
    if (fromPayload) return fromPayload;
    const fromProperties = String(lineItem?.properties?._globalId || lineItem?.properties?._globalId || '').trim();
    if (fromProperties) return fromProperties;
    return '';
  };

  const toCartGlobalIdMap = (cart) => {
    const globalIdMap = new Map();
    const items = Array.isArray(cart?.items) ? cart.items : [];
    for (const item of items) {
      const variantId = parseInt(String(item?.variant_id || ''), 10);
      if (!Number.isFinite(variantId)) continue;
      const globalId = getLineGlobalCustomId(item);
      if (!globalIdMap.has(variantId) || globalId !== '') {
        globalIdMap.set(variantId, globalId);
      }
    }
    return globalIdMap;
  };

  let sharedCartBaselineQuantities = null;
  let sharedCartBaselineGlobalIds = null;

  const bindSharedCartUpdateAndNavigationTracking = (cartToken) => {
    const token = String(cartToken || '').trim();
    if (!token) return;
    if (window.sharedCartTrackingBound === token) return;
    window.sharedCartTrackingBound = token;

    document.addEventListener('customCartLoaded', async () => {
      const context = getSharedCartSessionContext();
      if (!context?.cart_token) return;
      const cartData = window.JPCartService
        ? await window.JPCartService.getCart().catch(() => null)
        : await fetch('/cart.js').then(r => (r.ok ? r.json() : null)).catch(() => null);
      if (!cartData) return;
      sharedCartBaselineQuantities = toCartQuantityMap(cartData);
      sharedCartBaselineGlobalIds = toCartGlobalIdMap(cartData);
    }, { once: true });

    document.querySelector('cart-form')?.addEventListener('cart-updated', async () => {
      const context = getSharedCartSessionContext();
      if (!context?.cart_token) return;
      const cartData = window.JPCartService
        ? await window.JPCartService.getCart({ force: true }).catch(() => null)
        : await fetch('/cart.js').then(r => (r.ok ? r.json() : null)).catch(() => null);
      if (!cartData) return;

      const nextQuantities = toCartQuantityMap(cartData);
      const nextGlobalIds = toCartGlobalIdMap(cartData);
      const prevQuantities = sharedCartBaselineQuantities || new Map();
      const prevGlobalIds = sharedCartBaselineGlobalIds || new Map();
      const variantIds = new Set([...prevQuantities.keys(), ...nextQuantities.keys()]);
      const changes = [];

      for (const variantId of variantIds) {
        const prevQty = Number(prevQuantities.get(variantId) || 0);
        const nextQty = Number(nextQuantities.get(variantId) || 0);
        if (prevQty === nextQty) continue;
        const globalProductId = String(
          nextGlobalIds.get(variantId) ||
          prevGlobalIds.get(variantId) ||
          ''
        ).trim();
        if (!globalProductId) continue;
        if (prevQty > 0 && nextQty === 0) {
          changes.push({ global_custom_id: globalProductId, action: 'removed', previous_quantity: prevQty, current_quantity: nextQty });
        } else if (nextQty > prevQty) {
          changes.push({ global_custom_id: globalProductId, action: 'qty_increase', previous_quantity: prevQty, current_quantity: nextQty });
        } else {
          changes.push({ global_custom_id: globalProductId, action: 'qty_decrease', previous_quantity: prevQty, current_quantity: nextQty });
        }
      }

      sharedCartBaselineQuantities = nextQuantities;
      sharedCartBaselineGlobalIds = nextGlobalIds;
      if (changes.length === 0) return;

      pushSharedCartAnalyticsEvent('shared_cart_update_event', {
        cart_token: context.cart_token,
        market: context.destination_market,
        language: context.destination_language,
        partner_id: context.partner_id,
        changes
      });
    });

    document.addEventListener('click', (event) => {
      const context = getSharedCartSessionContext();
      if (!context?.cart_token) return;
      const checkoutTrigger = event.target.closest('button[name="checkout"], .guest-user-checkout-button, #custom-checkout-button, [data-js-cart-submit], a[href*="/checkout"]');
      if (!checkoutTrigger) return;
      const dedupeKey = buildSharedCartTrackingKey(context.cart_token, 'shared_cart_navigation_event', 'checkout');
      pushSharedCartAnalyticsEvent('shared_cart_navigation_event', {
        cart_token: context.cart_token,
        partner_id: context.partner_id,
        click_event: 'checkout'
      }, dedupeKey);
    });

    window.addEventListener('beforeunload', () => {
      const context = getSharedCartSessionContext();
      if (!context?.cart_token) return;
      const dedupeKey = buildSharedCartTrackingKey(context.cart_token, 'shared_cart_navigation_event', 'exit');
      pushSharedCartAnalyticsEvent('shared_cart_navigation_event', {
        cart_token: context.cart_token,
        partner_id: context.partner_id,
        click_event: 'exit'
      }, dedupeKey);
    });
  };

  const trackLoadedSharedCart = ({ cartToken, sharedCartData, unavailableVariantIds = [], priceChangeVariantIds = [] } = {}) => {
    const token = String(cartToken || '').trim();
    if (!token || !sharedCartData) return;

    const sourceMarket = String(getSourceMarketForAnalytics(sharedCartData) || '').toLowerCase();
    const destinationMarket = String(getDestinationMarketForAnalytics() || '').toLowerCase();
    const sourceLanguage = String(getSourceLanguage(sharedCartData) || '').toLowerCase();
    const destinationLanguage = String(getDestinationLanguage() || '').toLowerCase();
    const partnerId = getPartnerIdForAnalytics(sharedCartData);

    const unavailableProducts = getUnavailableGlobalCustomIds(sharedCartData, unavailableVariantIds);
    const priceChangeProducts = getPriceChangeGlobalCustomIds(sharedCartData, priceChangeVariantIds);

    setSharedCartSessionContext({
      cart_token: token,
      source_market: sourceMarket,
      destination_market: destinationMarket,
      source_language: sourceLanguage,
      destination_language: destinationLanguage,
      partner_id: partnerId
    });

    const viewEventDedupeKey = buildSharedCartTrackingKey(token, 'shared_cart_view_event');
    pushSharedCartAnalyticsEvent('shared_cart_view_event', {
      cart_token: token,
      market: destinationMarket,
      language: destinationLanguage,
      source_market: sourceMarket,
      destination_market: destinationMarket,
      source_language: sourceLanguage,
      destination_language: destinationLanguage,
      language_changed: sourceLanguage !== '' && destinationLanguage !== '' && sourceLanguage !== destinationLanguage,
      partner_id: partnerId,
      unavailable_products: unavailableProducts,
      price_change_products: priceChangeProducts
    }, viewEventDedupeKey);

    bindSharedCartUpdateAndNavigationTracking(token);
  };

  window.SharedCartTracking = {
    trackLoadedSharedCart
  };
})();
